/**
 * TaxSetu AI - Google OAuth Callback Page
 * ========================================
 * Handles the redirect from Google OAuth and exchanges code for token.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleGoogleCallback } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [error, setError] = useState('');

  useEffect(() => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    
    if (error) {
      setStatus('error');
      setError(error === 'access_denied' ? 'You cancelled the sign-in process.' : error);
      setTimeout(() => navigate('/chat'), 3000);
      return;
    }
    
    if (!code) {
      setStatus('error');
      setError('No authorization code received');
      setTimeout(() => navigate('/chat'), 3000);
      return;
    }
    
    // Exchange code for token
    handleGoogleCallback(code)
      .then((user) => {
        setStatus('success');
        // Redirect to chat after success
        setTimeout(() => navigate('/chat'), 1500);
      })
      .catch((err) => {
        setStatus('error');
        setError(err.message || 'Authentication failed');
        setTimeout(() => navigate('/chat'), 3000);
      });
  }, [searchParams, handleGoogleCallback, navigate]);

  return (
    <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
      <div className="bg-[#0f2744] rounded-2xl p-8 max-w-md w-full mx-4 text-center shadow-xl border border-[#1e3a5f]">
        {/* Logo */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">
            Tax<span className="text-[#38bdf8]">Setu</span> AI
          </h1>
        </div>
        
        {status === 'processing' && (
          <>
            <Loader2 className="w-12 h-12 text-[#38bdf8] animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Signing you in...</h2>
            <p className="text-gray-400">Please wait while we complete your authentication.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Welcome to TaxSetu AI!</h2>
            <p className="text-gray-400">Redirecting you to the chat...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Authentication Failed</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <p className="text-sm text-gray-500">Redirecting you back...</p>
          </>
        )}
      </div>
    </div>
  );
}
